import { Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { _ } from '/@/locale';
import MuiDatePicker from '/@/mui/datepicker';
import { MuiSelect } from '/@/mui/select';
import type { DensityRecommendationForm } from '/@/types/density-recommendation';
import { createPlantingDateValidator } from '/@/utils/date';

const EnvironmentConditionsStep = ({
    form,
    soilMoistureChoices,
    showSoilMoisture = true,
    climatePatternChoices,
    validDates,
}: {
    form: UseFormReturn<DensityRecommendationForm>;
    soilMoistureChoices: { label: string; value: string }[];
    showSoilMoisture?: boolean;
    climatePatternChoices: { label: string; value: string }[];
    validDates: [string, string][] | undefined;
}) => {
    const shouldDisableDate = useMemo(() => createPlantingDateValidator(validDates), [validDates]);

    const formFields = [
        {
            title: _('Fecha de siembra'),
            fieldKey: 'plantingDate',
            component: (
                <MuiDatePicker
                    value={form.watch('plantingDate') ? dayjs(form.watch('plantingDate')) : undefined}
                    onChange={(e) => {
                        form.setValue('plantingDate', e ? e.format('YYYY-MM-DD') : '');
                    }}
                    label={
                        <Typography color={form.getValues().plantingDate ? 'primary' : 'primary.mui.inactive'}>
                            {_('Fecha de siembra')}
                        </Typography>
                    }
                    sx={{
                        width: { xs: '100%', md: 300 },
                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: (t) => t.palette.primary.main,
                        },
                        '& .MuiInputBase-input': {
                            color: (t) => t.palette.primary.main,
                        },
                        '& .MuiSvgIcon-root': {
                            color: (t) => t.palette.primary.main,
                        },
                    }}
                    shouldDisableDate={shouldDisableDate}
                />
            ),
        },
        ...(showSoilMoisture
            ? [
                  {
                      title: _('Humedad del suelo'),
                      description: _('Podés consultar el dato de una zona en el mapa de humedad'),
                      fieldKey: 'soilMoisture',
                      component: (
                          <MuiSelect
                              inputLabel={_('Agua en el suelo')}
                              sx={{ width: { xs: '100%', md: 300 } }}
                              value={form.watch('soilMoisture')?.toString() || ''}
                              onChange={(e) => {
                                  form.setValue('soilMoisture', Number(e.target.value));
                              }}
                              options={soilMoistureChoices.map((choice) => ({
                                  ...choice,
                                  value: choice.value.toString(),
                              }))}
                          />
                      ),
                  },
              ]
            : []),
        {
            title: _('Año climático'),
            fieldKey: 'climatePattern',
            component: (
                <MuiSelect
                    inputLabel={_('Año climático')}
                    sx={{ width: { xs: '100%', md: 300 } }}
                    value={form.watch('climatePattern')?.toString() || ''}
                    onChange={(e) => {
                        form.setValue('climatePattern', Number(e.target.value));
                    }}
                    options={climatePatternChoices}
                />
            ),
        },
    ];
    return (
        <Stack spacing={2}>
            {formFields.map((field) => (
                <Stack key={field.fieldKey} spacing={1} direction='column'>
                    <Typography color='primary' variant='body1' fontWeight={700}>
                        {field.title}
                    </Typography>
                    {field.component}
                </Stack>
            ))}
        </Stack>
    );
};

export default EnvironmentConditionsStep;
